/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'

import {
  SABRE_GET_PRODUCT_MODEL_EXCLUSION,
  SABRE_SET_PRODUCT_EXCLUDED_FROM_MODELS,
  type SabreGetProductModelExclusionQuery,
  type SabreGetProductModelExclusionQueryVariables,
  type SabreSetProductExcludedFromModelsMutation,
  type SabreSetProductExcludedFromModelsMutationVariables,
} from '@dai/graphql'
import useT from '@/i18n/useT'

type ProductModelExclusionControlProps = {
  upc: string
}

const ProductModelExclusionControl: React.FC<
  ProductModelExclusionControlProps
> = ({ upc }) => {
  const { t } = useT()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<
    SabreGetProductModelExclusionQuery,
    SabreGetProductModelExclusionQueryVariables
  >(SABRE_GET_PRODUCT_MODEL_EXCLUSION, {
    variables: { upc },
    context: { endPoint: 'sabre' },
    fetchPolicy: 'network-only',
    skip: !upc,
  })
  const [setExcluded, { loading }] = useMutation<
    SabreSetProductExcludedFromModelsMutation,
    SabreSetProductExcludedFromModelsMutationVariables
  >(SABRE_SET_PRODUCT_EXCLUDED_FROM_MODELS, {
    context: { endPoint: 'sabre' },
  })

  const product = data?.product
  if (queryError && !product) {
    return (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      >
        {t('productModelExclusion.loadFailed', {
          message: queryError.message,
          defaultValue: `Couldn't load the product's model exclusion: ${queryError.message}`,
        })}
      </Alert>
    )
  }
  if (!product && queryLoading) {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled
        startIcon={<CircularProgress size={14} />}
      >
        {t('productModelExclusion.loading', 'Checking model exclusion…')}
      </Button>
    )
  }
  if (!product) return null
  const excluded = product.excludedFromModels

  const closeDialog = () => {
    setDialogOpen(false)
    setError(null)
  }

  const submit = async () => {
    try {
      await setExcluded({
        variables: {
          input: { upc, excluded: !excluded },
        },
      })
      closeDialog()
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t(
              'productModelExclusion.error',
              'Could not update the product. Try again.',
            ),
      )
    }
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        {excluded && (
          <Chip
            color="warning"
            label={t(
              'productModelExclusion.excludedChip',
              'Excluded from models',
            )}
          />
        )}
        <Button
          size="small"
          variant="outlined"
          color={excluded ? 'primary' : 'warning'}
          onClick={() => setDialogOpen(true)}
        >
          {excluded
            ? t('productModelExclusion.includeButton', 'Include in models')
            : t(
                'productModelExclusion.excludeButton',
                'Exclude product from models',
              )}
        </Button>
      </Stack>
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {excluded
            ? t('productModelExclusion.includeTitle', {
                name: `${product.preferredName} ${product.packagingName}`,
                defaultValue: `Include ${product.preferredName} ${product.packagingName} in models again?`,
              })
            : t('productModelExclusion.excludeTitle', {
                name: `${product.preferredName} ${product.packagingName}`,
                defaultValue: `Exclude ${product.preferredName} ${product.packagingName} from models?`,
              })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              {excluded
                ? t(
                    'productModelExclusion.includeDescription',
                    'The product becomes a model class again in the next DAI Zero update, if it still has enough recent crops.',
                  )
                : t(
                    'productModelExclusion.excludeDescription',
                    'The next DAI Zero update drops this product as a model class and forgets its crops. Its crops and labels are not changed, so it can be included again later.',
                  )}
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            color={excluded ? 'primary' : 'warning'}
            disabled={loading}
            onClick={submit}
          >
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ProductModelExclusionControl
